import React, { useState, useEffect, useRef } from 'react';
import { Alert, Spinner, ListGroup, Button } from 'react-bootstrap';

/**
 * ValidationStep - Step 8: Run cross-system validation checks.
 * Validates the first 6 checks (pre-enablement). The is_iris_enabled check
 * is excluded here since enablement happens in step 9.
 */
const ValidationStep = ({ wizard, api }) => {
  const [result, setResult] = useState(null);
  const hasTriggered = useRef(false);

  useEffect(() => {
    if (!hasTriggered.current && wizard.sessionId) {
      hasTriggered.current = true;
      runValidation();
    }
  }, [wizard.sessionId]);

  const runValidation = async () => {
    try {
      const data = await api.validateProvisioning(wizard.sessionId);
      // Filter out the is_iris_enabled check — that's handled by step 9 (Enable)
      const preEnableChecks = (data.checks || []).filter(c => c.name !== 'IDC is_iris_enabled');
      // Normalize status (backend returns numbers: 0=Passed, 1=Failed, 2=UnableToVerify)
      const normalizeStatus = (s) => typeof s === 'number' ? ['Passed', 'Failed', 'UnableToVerify'][s] || 'Unknown' : s;
      const normalizedChecks = preEnableChecks.map(c => ({ ...c, status: normalizeStatus(c.status) }));
      const allPreEnablePassed = normalizedChecks.every(c => c.status === 'Passed');

      setResult({ ...data, checks: normalizedChecks, allPassed: allPreEnablePassed });
      if (allPreEnablePassed) {
        wizard.completeStep(8);
      }
    } catch {
      // Error shown by hook
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Passed': return <span style={{ fontSize: '1.2rem' }}>✅</span>;
      case 'Failed': return <span style={{ fontSize: '1.2rem' }}>❌</span>;
      case 'UnableToVerify': return <span style={{ fontSize: '1.2rem' }}>⚠️</span>;
      default: return <span>❓</span>;
    }
  };

  if (api.loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" className="mb-3" />
        <p>Running validation checks...</p>
      </div>
    );
  }

  if (api.error) {
    return (
      <div>
        <Alert variant="danger">{api.error}</Alert>
        <Button variant="outline-primary" onClick={() => { hasTriggered.current = false; runValidation(); }}>
          Re-validate
        </Button>
      </div>
    );
  }

  if (result) {
    const failedChecks = result.checks.filter(c => c.status !== 'Passed');

    return (
      <div>
        <Alert variant={result.allPassed ? 'success' : 'danger'}>
          <Alert.Heading>{result.allPassed ? 'All Checks Passed ✅' : 'Some Checks Failed'}</Alert.Heading>
          <p>{result.allPassed
            ? 'All pre-enablement validations passed. Click "Next" to proceed to customer enablement.'
            : 'The following issues must be resolved before enabling this customer.'}
          </p>
        </Alert>

        <ListGroup className="mb-3">
          {result.checks.map((check, i) => (
            <ListGroup.Item
              key={i}
              className="d-flex justify-content-between align-items-center"
              style={{ backgroundColor: check.status === 'Passed' ? '#f0fff4' : check.status === 'Failed' ? '#fff5f5' : '#fffbeb' }}
            >
              <div>
                <strong>{check.name}</strong>
                {check.status === 'Passed' && check.actualState && (
                  <div className="text-muted small">{check.actualState}</div>
                )}
                {check.status === 'Failed' && (
                  <>
                    <div className="text-danger small">Expected: {check.expectedState}</div>
                    <div className="text-danger small">Actual: {check.actualState}</div>
                    {check.message && <div className="text-danger small mt-1"><em>{check.message}</em></div>}
                  </>
                )}
                {check.status === 'UnableToVerify' && (
                  <div className="text-warning small">{check.message || 'Could not connect to verify'}</div>
                )}
              </div>
              {getStatusIcon(check.status)}
            </ListGroup.Item>
          ))}
        </ListGroup>

        {!result.allPassed && (
          <>
            <Alert variant="light" className="border">
              <strong>What to fix:</strong>
              <ul className="mb-0 mt-1">
                {failedChecks.map((check, i) => (
                  <li key={i}>{check.name}: {check.actualState || check.message}</li>
                ))}
              </ul>
            </Alert>
            <Button variant="outline-primary" className="mt-3" onClick={() => { hasTriggered.current = false; runValidation(); }}>
              Re-validate
            </Button>
          </>
        )}
      </div>
    );
  }

  return null;
};

export default ValidationStep;
